import type { Category, CategoryFilter, CategoryPage } from '~/types/database'
import type { CategorySlug } from '~/constants/categories'
import { VALID_CATEGORY_SLUGS } from '~/constants/categories'

interface CategoryCache {
  data: CategoryPage | null
  timestamp: number
}

// Cache TTL: 15 minutes
const CACHE_TTL = 15 * 60 * 1000

// Client-side only cache (SSR-safe: module-level cache only used on client)
// On server, each request gets fresh data via useAsyncData
let categoryCache: Map<string, CategoryCache> | null = null

const getCache = (): Map<string, CategoryCache> => {
  // Only use module-level cache on client side
  if (import.meta.client) {
    if (!categoryCache) {
      categoryCache = new Map()
    }
    return categoryCache
  }
  // On server, return empty map (no caching across requests)
  return new Map()
}

/**
 * Composable for fetching category page data with filters
 * Uses useAsyncData for SSR support
 */
export const useCategoryPage = (slug: MaybeRef<CategorySlug>) => {
  const supabase = useSupabaseClient()

  /**
   * Check if cache entry is still valid
   */
  const isCacheValid = (entry: CategoryCache): boolean => {
    return Date.now() - entry.timestamp < CACHE_TTL
  }

  /**
   * Fetch category with its filters from database
   * Uses separate queries to avoid PostgREST schema cache issues
   */
  const fetchCategory = async (categorySlug: string): Promise<CategoryPage | null> => {
    // Check cache first (client-side only)
    const cache = getCache()
    const cached = cache.get(categorySlug)
    if (cached && isCacheValid(cached)) {
      return cached.data
    }

    try {
      // Step 1: Fetch the category
      const { data: categoryData, error: categoryError } = await supabase
        .from('categories')
        .select('*')
        .eq('slug', categorySlug)
        .single()

      if (categoryError) {
        if (categoryError.code === 'PGRST116') {
          return null // Not found
        }
        throw categoryError
      }

      if (!categoryData) {
        return null
      }

      // Step 2: Fetch filters for this category (separate query)
      let filters: CategoryFilter[] = []
      try {
        const { data: filtersData } = await supabase
          .from('category_filters')
          .select('*')
          .eq('category_id', categoryData.id)
          .order('display_order')

        if (filtersData && filtersData.length > 0) {
          // Step 3: Fetch options for all filters
          const filterIds = filtersData.map(f => f.id)
          const { data: optionsData } = await supabase
            .from('filter_options')
            .select('*')
            .in('category_filter_id', filterIds)
            .order('display_order')

          // Map options to their filters
          filters = filtersData.map(filter => ({
            ...filter,
            options: (optionsData || []).filter(opt => opt.category_filter_id === filter.id)
          })) as CategoryFilter[]
        }
      } catch (e) {
        // If filters query fails, continue with empty filters
        console.warn('Failed to fetch filters:', e)
      }

      // Build the category page object
      const categoryPage: CategoryPage = {
        ...categoryData,
        filters
      }

      // Cache the result (client-side only)
      cache.set(categorySlug, {
        data: categoryPage,
        timestamp: Date.now()
      })

      return categoryPage
    } catch (e) {
      console.error('Error fetching category:', e)

      // Return stale cache if available
      if (cached) {
        return cached.data
      }

      throw e
    }
  }

  // Use useAsyncData for SSR-compatible data fetching
  const slugValue = computed(() => unref(slug))

  const { data: category, pending: loading, error, refresh } = useAsyncData(
    () => `category-page-${slugValue.value}`,
    async () => {
      const currentSlug = slugValue.value

      // Validate slug
      if (!VALID_CATEGORY_SLUGS.includes(currentSlug)) {
        throw createError({
          statusCode: 404,
          message: `Invalid category: ${currentSlug}`
        })
      }

      const result = await fetchCategory(currentSlug)

      if (!result) {
        throw createError({
          statusCode: 404,
          message: `Category not found: ${currentSlug}`
        })
      }

      return result
    },
    {
      // Re-fetch when slug changes
      watch: [slugValue]
    }
  )

  /**
   * Clear all category cache
   */
  const clearCache = () => {
    getCache().clear()
  }

  return {
    category: category as Ref<CategoryPage | null>,
    loading,
    error,
    refresh,
    clearCache
  }
}

/**
 * Fetch all categories for navigation (simpler, with longer cache)
 */
export const useNavigationCategories = () => {
  const supabase = useSupabaseClient()

  return useAsyncData('navigation-categories', async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name, slug, is_premium_brand, brand_color, display_order')
      .in('slug', VALID_CATEGORY_SLUGS)
      .order('display_order')

    if (error) {
      console.error('Error fetching navigation categories:', error)
      return []
    }

    return data as Pick<Category, 'id' | 'name' | 'slug' | 'is_premium_brand' | 'brand_color' | 'display_order'>[]
  }, {
    // Cache in Nuxt payload for SSR
    getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] || nuxtApp.static.data[key]
  })
}
